import { useApolloClient } from "@apollo/client";
import { Box, Stack, useTheme } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { SubNavAppBar } from "components/AppBar";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { ShippingPaperworkQuery } from "generated-graphql/graphql";
import {
  GridSelectionProvider,
  OmnisearchDataGridV2,
  useGridSelection,
  useOmnisearchStore,
} from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags, useStoredFeatureFlags } from "hooks/useFeatureFlags";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { ManifestDetailDialog } from "routes/Staff/WasteFulfillment/ManifestReview/ManifestDetailDialog";
import { stateOptionLabel } from "util/filterOptionLabels";

import { YearAutocompletePicker } from "../ManifestLineItems/YearAutocompletePIcker";
import { SHIPPING_DOCUMENT_KIND_LABELS } from "../ShippingDocuments/shippingDocumentDisplay";
import { AddShippingRecordButton } from "./AddShippingRecordButton";
import { SHIPPING_PAPERWORK_QUERY } from "./api";
import { BulkReviewDiscrepanciesButton } from "./BulkReviewDiscrepanciesButton";
import { ManifestDetails } from "./ManifestDetails";
import { Row, useManifestColumns } from "./Manifests";
import { ManifestsExportMenu } from "./ManifestsExportMenu";
import { MergedManifestShowFilterPicker } from "./ManifestShowFilterPicker";
import { manifestsV3DefaultSearch } from "./manifestsV3DefaultSearch";
import { manifestsV3RowClickHandler } from "./manifestsV3RowDestination";
import { selectedManifestIds } from "./manifestsV3Selection";
import { mergeDefaultAndOmnisearch } from "./mergeDefaultAndOmnisearch";
import { ShippingDocumentDrawer } from "./ShippingDocumentDrawer";
import {
  BILL_OF_LADING_COLUMNS,
  blankOnBillOfLading,
  buildPaperworkRow,
  INITIAL_COLUMN_VISIBILITY,
  isPaperworkRow,
  kindColumn,
  MOBILE_COLUMN_VISIBILITY,
  PAPERWORK_KEY_ALIASES,
  paperworkManifestId,
  toManifestExportSearch,
  toPaperworkSortModel,
  withPaperworkQuerySupport,
} from "./shippingPaperworkColumns";
import {
  searchIncludesManifests,
  shippingPaperworkKindValues,
} from "./shippingPaperworkKindFilter";
import { useManifestsV3RowDrawers } from "./useManifestsV3RowDrawers";
import { VendorDashboard } from "./VendorDashboard/VendorDashboard";
import { VendorDashboardToggle } from "./VendorDashboard/VendorDashboardToggle";

// The grid's own column name, matching V2's initial sort. `toPaperworkSortModel`
// translates it to the view's `shipDate` through PAPERWORK_KEY_ALIASES on the
// way to the query. Naming the view's column here instead would leave MUI
// looking for a column that does not exist, so the grid would open with no sort
// indicator and the persisted value could not be cleared by clicking.
/** The Type column's own labels, keyed for the filter control. */
const KIND_LABEL_BY_VALUE = new Map<string, string>(Object.entries(SHIPPING_DOCUMENT_KIND_LABELS));

const INITIAL_SORT: GridSortModel = [{ field: "shippedDate", sort: "desc" }];

const getItems = (data: ShippingPaperworkQuery) =>
  (data.shippingPaperwork?.items ?? []).map(buildPaperworkRow);
const getCount = (data: ShippingPaperworkQuery) => data.shippingPaperwork?.count ?? 0;

// An item's own `id` is already `<kind>:<entity id>`, so it is unique across
// the two source tables' independent uuid spaces. Handed to the grid
// explicitly rather than left to its default, so the row identity stays a
// stated choice.
const getRowId = (row: Row) => row.id;

/**
 * Only a manifest row can be selected. The bulk action behind the checkboxes
 * (Review Discrepancies) reads manifest discrepancies, which a bill of lading
 * has none of — so a bill-of-lading row offers no checkbox rather than one that
 * contributes nothing to the action.
 */
const isManifestRowSelectable = (params: GridRowParams<Row>) =>
  paperworkManifestId(params.row) !== null;

/** The earliest year the picker offers, matching V2's Manifests screen. */
const MIN_YEAR = 2018;

/**
 * Manifests and bills of lading in one sortable list. Renders only behind
 * `useManifestViewsV3`; `ManifestsView` picks it over the V1/V2 manifest-only
 * views when the flag is on.
 *
 * The columns are V2's own (`useManifestColumns`), rendered against a row whose
 * manifest side has been flattened onto it — so this list carries V2's filters,
 * cells and default visibility without a second set of definitions to keep in
 * step. `withPaperworkQuerySupport` then removes the sorts and filters the
 * merged view cannot answer, rather than leaving a control that does nothing.
 *
 * `kind` is registered as an omnisearch filter key (not a grid column) so a
 * Type filter survives a page reload and round-trips through the URL, the way
 * every other filter on this screen does.
 */
export function ManifestsV3() {
  const { tenantId } = useTenant();
  const { selectedId: processedManifestId, popIdFromUrl } =
    useSelectedIdInUrl("processedManifestId");
  // A manifest row and a bill-of-lading row both open in place over the grid:
  // a manifest opens the same URL-driven drawer V2's Manifests page opens
  // (path segment `processedManifestId`, above); a bill of lading opens its
  // own drawer (query param `shippingDocumentId`, owned by this hook). See
  // useManifestsV3RowDrawers for how opening one clears the other.
  const { shippingDocumentId, openManifest, openBillOfLading, closeShippingDocument } =
    useManifestsV3RowDrawers(processedManifestId);
  const {
    isStaff,
    showContainerTracking,
    showDiscrepancies,
    showStaffDiscrepancyControls,
    showSourceDocument,
  } = useManifestsV3Gates();

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    // Matches the nav entry, which customerNavSectionsV2 renames under this
    // same flag. A literal rather than another flag read: this component only
    // renders when manifest-views-v3 is on.
    { label: "Shipment Documents" },
  ]);

  const [discrepancyRow, setDiscrepancyRow] = useState<Row | null>(null);

  const { shippedDateYear, setShippedDateYear, defaultSearch, showFilterDefaultSearch } =
    useManifestsV3PageScope(tenantId ?? "");

  const handleDiscrepancyClick = useCallback((row: Row) => setDiscrepancyRow(row), []);

  const { columns, filterKeys } = useMergedPaperworkGrid({
    showDiscrepancies,
    showSourceDocument,
    showContainerTracking,
    showStaffDiscrepancyControls,
    isStaff,
    onDiscrepancyClick: handleDiscrepancyClick,
  });

  const { buildVariables, handleRowClick } = useMergedGridCallbacks({
    defaultSearch,
    openManifest,
    openBillOfLading,
  });

  const { startCommandButtons, commandButtons } = useMergedToolbarButtons({
    tenantId: tenantId ?? "",
    showFilterDefaultSearch,
    shippedDateYear,
    showStaffDiscrepancyControls,
    isStaff,
  });

  if (!tenantId) {
    return null;
  }

  return (
    <Box>
      <ManifestsV3Chrome shippedDateYear={shippedDateYear} onYearChange={setShippedDateYear} />
      <VendorDashboard shippedDateYear={shippedDateYear} />
      {/*
        Selection lives in the grid and is read through `useGridSelection` by
        the two toolbar controls that care (bulk review, export). The provider
        has to wrap both the grid and those controls, which the toolbar renders
        as descendants of the grid.
      */}
      <GridSelectionProvider>
        <OmnisearchDataGridV2
          persistenceKey="waste-manifests-v3"
          columns={columns}
          additionalFilterKeys={filterKeys}
          keyAliases={PAPERWORK_KEY_ALIASES}
          query={SHIPPING_PAPERWORK_QUERY}
          getItems={getItems}
          getCount={getCount}
          getRowId={getRowId}
          buildVariables={buildVariables}
          noRowsMessage="No manifests or bills of lading have been added to this organization."
          initialSortModel={INITIAL_SORT}
          initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY}
          mobileColumnVisibilityModel={MOBILE_COLUMN_VISIBILITY}
          startCommandButtons={startCommandButtons}
          commandButtons={commandButtons}
          onRowClick={handleRowClick}
          // Same staff gate V2 puts its checkboxes behind: the only bulk action
          // on this grid is the staff discrepancy review.
          checkboxSelection={showStaffDiscrepancyControls}
          isRowSelectable={isManifestRowSelectable}
          disableRowSelectionOnClick
          tenantId={tenantId}
        />
      </GridSelectionProvider>
      <ManifestsV3Overlays
        processedManifestId={processedManifestId}
        onCloseManifest={popIdFromUrl}
        shippingDocumentId={shippingDocumentId}
        onCloseShippingDocument={closeShippingDocument}
        showDiscrepancies={showDiscrepancies}
        discrepancyRow={discrepancyRow}
        isStaff={isStaff}
        onCloseDiscrepancies={() => setDiscrepancyRow(null)}
      />
    </Box>
  );
}

/**
 * The three surfaces that open over the grid: a manifest's drawer, a bill of
 * lading's drawer, and the discrepancy modal a Discrepancies cell opens.
 */
function ManifestsV3Overlays({
  processedManifestId,
  onCloseManifest,
  shippingDocumentId,
  onCloseShippingDocument,
  showDiscrepancies,
  discrepancyRow,
  isStaff,
  onCloseDiscrepancies,
}: {
  processedManifestId: string | undefined;
  onCloseManifest: () => void;
  shippingDocumentId: string | undefined;
  onCloseShippingDocument: () => void;
  showDiscrepancies: boolean;
  discrepancyRow: Row | null;
  isStaff: boolean;
  onCloseDiscrepancies: () => void;
}) {
  return (
    <>
      <ManifestDetails
        processedManifestId={processedManifestId}
        open={!!processedManifestId}
        onClose={onCloseManifest}
      />
      <ShippingDocumentDrawer
        shippingDocumentId={shippingDocumentId}
        open={!!shippingDocumentId}
        onClose={onCloseShippingDocument}
      />
      {showDiscrepancies && (
        <DiscrepancyDialog row={discrepancyRow} isStaff={isStaff} onClose={onCloseDiscrepancies} />
      )}
    </>
  );
}

/**
 * The grid's two scope-carrying callbacks: what it queries, and where a row
 * click goes.
 */
function useMergedGridCallbacks({
  defaultSearch,
  openManifest,
  openBillOfLading,
}: {
  defaultSearch: string;
  openManifest: (manifestId: string) => void;
  openBillOfLading: (shippingDocumentId: string) => void;
}) {
  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      // Merged rather than concatenated: the year is a default scope, so a
      // user's own `shipDate:` filter replaces it instead of ANDing with it.
      search: mergeDefaultAndOmnisearch(defaultSearch, omnisearch),
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toPaperworkSortModel(sort),
    }),
    [defaultSearch]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => {
      // The grid is typed on V2's row so V2's columns can be reused as-is; the
      // paperwork fields the routing reads are recovered by the guard.
      if (!isPaperworkRow(params.row)) return;
      manifestsV3RowClickHandler(
        { id: params.id, row: params.row },
        { openManifest, openBillOfLading }
      );
    },
    [openManifest, openBillOfLading]
  );

  return { buildVariables, handleRowClick };
}

/**
 * Who this viewer is and which of the screen's surfaces that opens.
 *
 * `showContainerTracking` and `showDiscrepancies` are resolved the staff-aware
 * way `useFeatureFlags` resolves every flag (server value OR staff override) —
 * the same mechanism the `shippingPaperwork` resolver's own `getFeatureFlag`
 * call uses server-side, so the Type filter's options and the resolver's actual
 * gate can't disagree. `showSourceDocument` reads the STORED waste-module value
 * instead, mirroring V2's Manifests grid exactly: a staff user viewing as a
 * customer must not see a Document Review column that would break on a tenant
 * the backend doesn't ingest manifests for.
 */
function useManifestsV3Gates() {
  const { featureFlags } = useFeatureFlags();
  const { featureFlags: storedFlags } = useStoredFeatureFlags();
  const { isStaff } = useCurrentUser();

  const showDiscrepancies = !!featureFlags[FeatureFlagKey.WasteModule];

  return {
    isStaff,
    showContainerTracking: !!featureFlags[FeatureFlagKey.ContainerTracking],
    showDiscrepancies,
    showStaffDiscrepancyControls: isStaff && showDiscrepancies,
    showSourceDocument: isStaff && !!storedFlags[FeatureFlagKey.WasteModule],
  };
}

/**
 * The year picker's state and the two hidden default searches it scopes.
 *
 * Two vocabularies for one date: the Show picker's counts come from the
 * manifest-only `processedManifestShowFilterCounts` query (`shippedDate`), the
 * grid's rows from the merged view (`shipDate`, a column bills of lading carry
 * too — so on this list the year scopes both kinds, which is what a merged list
 * should do).
 */
function useManifestsV3PageScope(tenantId: string) {
  // Same localStorage key V2's Manifests screen uses, so a year chosen on one
  // screen is the year the other opens on.
  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-shipped-date-year",
    { defaultValue: DateTime.now().year }
  );

  // The active search, read for the year scope's yield rule alone. The grid
  // reads its own copy through buildVariables.
  const { omnisearch } = useOmnisearchStore();

  const showFilterDefaultSearch = useMemo(
    () =>
      manifestsV3DefaultSearch({
        tenantId,
        shippedDateYear,
        omnisearch,
        shipDateKey: "shippedDate",
      }),
    [tenantId, shippedDateYear, omnisearch]
  );
  const defaultSearch = useMemo(
    () =>
      manifestsV3DefaultSearch({ tenantId, shippedDateYear, omnisearch, shipDateKey: "shipDate" }),
    [tenantId, shippedDateYear, omnisearch]
  );

  return { shippedDateYear, setShippedDateYear, defaultSearch, showFilterDefaultSearch };
}

/**
 * The page chrome above the dashboard and the grid: the year picker on the
 * left, the vendor-stats toggle on the right, exactly as V2's Manifests screen
 * lays them out. The Show picker sits in the grid toolbar on this screen rather
 * than beside the year.
 */
function ManifestsV3Chrome({
  shippedDateYear,
  onYearChange,
}: {
  shippedDateYear: number;
  onYearChange: (year: number) => void;
}) {
  const theme = useTheme();

  return (
    <SubNavAppBar
      sx={{ py: 2, px: 3, borderBottom: "1px solid", borderColor: theme.palette.divider }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box sx={{ width: 80 }}>
          <YearAutocompletePicker
            label="Year"
            minYear={MIN_YEAR}
            value={shippedDateYear}
            onChange={onYearChange}
          />
        </Box>
        <VendorDashboardToggle />
      </Stack>
    </SubNavAppBar>
  );
}

/**
 * The Show picker on the toolbar's left; the bulk review action, the add
 * control and the export menu on its right — the controls V2 carries above its
 * grid.
 */
function useMergedToolbarButtons({
  tenantId,
  showFilterDefaultSearch,
  shippedDateYear,
  showStaffDiscrepancyControls,
  isStaff,
}: {
  tenantId: string;
  showFilterDefaultSearch: string;
  shippedDateYear: number;
  showStaffDiscrepancyControls: boolean;
  isStaff: boolean;
}) {
  const startCommandButtons = useMemo(
    () => [
      <MergedManifestShowFilterPicker key="show-filter" defaultSearch={showFilterDefaultSearch} />,
    ],
    [showFilterDefaultSearch]
  );

  // The grid issues SHIPPING_PAPERWORK_QUERY through `useBackgroundQuery`, so
  // it is a watched query Apollo can re-run by document. An added manifest's
  // review applies through a mutation that refetches nothing, which left the
  // new row invisible until a manual reload.
  const client = useApolloClient();
  const refetchPaperwork = useCallback(() => {
    void client.refetchQueries({ include: [SHIPPING_PAPERWORK_QUERY] });
  }, [client]);

  const commandButtons = useCallback(
    ({ search }: { search: string }) => [
      ...(showStaffDiscrepancyControls
        ? [<BulkReviewDiscrepanciesButton key="review-discrepancies" />]
        : []),
      <AddShippingRecordButton
        key="add-shipping-record"
        tenantId={tenantId}
        onSaved={refetchPaperwork}
      />,
      // Gated here, on the grid's own untranslated search. The exports run the
      // manifest-only query and cannot express "no manifests", so a Type filter
      // excluding them would export the whole year from a grid showing none.
      // The question has to be asked before `toManifestExportSearch`, which
      // strips the very `kind:` term that answers it.
      ...(searchIncludesManifests(search)
        ? [
            <MergedExportMenu
              key="export"
              tenantId={tenantId}
              search={toManifestExportSearch(search)}
              shippedDateYear={shippedDateYear}
              showDiscrepancyCounts={showStaffDiscrepancyControls}
              showManifestPdfExport={isStaff}
            />,
          ]
        : []),
    ],
    [tenantId, shippedDateYear, showStaffDiscrepancyControls, isStaff, refetchPaperwork]
  );

  return { startCommandButtons, commandButtons };
}

/**
 * V2's export menu, with the grid's current selection reduced to manifest ids
 * for its selection-scoped options. Reads the selection here rather than in
 * `ManifestsV3` so ticking a checkbox re-renders this menu alone.
 */
function MergedExportMenu({
  tenantId,
  search,
  shippedDateYear,
  showDiscrepancyCounts,
  showManifestPdfExport,
}: {
  tenantId: string;
  search: string;
  shippedDateYear: number;
  showDiscrepancyCounts: boolean;
  showManifestPdfExport: boolean;
}) {
  const { getSelectedRows } = useGridSelection<Row>();

  return (
    <ManifestsExportMenu
      tenantId={tenantId}
      fileName={`waste-manifests-${shippedDateYear}`}
      search={search}
      selectedIds={selectedManifestIds(getSelectedRows())}
      shippedDateYear={shippedDateYear}
      showDiscrepancyCounts={showDiscrepancyCounts}
      showManifestPdfExport={showManifestPdfExport}
    />
  );
}

interface MergedPaperworkGridArgs {
  showDiscrepancies: boolean;
  showSourceDocument: boolean;
  showContainerTracking: boolean;
  showStaffDiscrepancyControls: boolean;
  isStaff: boolean;
  onDiscrepancyClick: (row: Row) => void;
}

/**
 * V2's columns and filters, adjusted to what the merged view can answer.
 *
 * Everything V2 shows, in V2's order, with the Type column ahead of it and the
 * bill-of-lading party columns behind it. The two page-level filter keys are
 * not grid columns: `kind` matches the document's own kind (the same value the
 * Type column prints), and `hasEpaCorrection` is a boolean the view registers
 * without a column of its own.
 */
function useMergedPaperworkGrid({
  showDiscrepancies,
  showSourceDocument,
  showContainerTracking,
  showStaffDiscrepancyControls,
  isStaff,
  onDiscrepancyClick,
}: MergedPaperworkGridArgs) {
  const filterKeys = useMemo<FilterKey[]>(
    () => [
      {
        header: "Type",
        key: "kind",
        filterKeyType: "enumAutocomplete",
        enumValues: shippingPaperworkKindValues(showContainerTracking),
        // Through the same label map the Type column prints from. Without it
        // FilterPopover falls back to prettyPrintEnumValue, which renders
        // "Bill Of Lading" while every grid row says "Bill of Lading".
        enumPresentationFunction: (value: string) => KIND_LABEL_BY_VALUE.get(value) ?? value,
      },
      // The merged view now answers `states` and `hasDiscrepancy` through its
      // `processedManifest` relation (mirroring V2's own builders), so both
      // are offered here exactly as V2 offers them. `primarySource` has no
      // control or column on V2's own Manifests list either, so nothing is
      // added for it. `hasSource` and `sourceDocumentState` ride the grid
      // columns V3 already inherits from `useManifestColumns`.
      {
        header: "State/Territory",
        key: "states",
        filterKeyType: "enumAutocomplete",
        enumValues: Object.keys(STATE_NAMES),
        enumPresentationFunction: stateOptionLabel,
      },
      ...(showDiscrepancies
        ? [
            {
              header: "Discrepancy",
              key: "hasDiscrepancy",
              filterKeyType: "boolean" as const,
            },
          ]
        : []),
      ...(showStaffDiscrepancyControls
        ? [
            {
              header: "EPA Correction Request",
              key: "hasEpaCorrection",
              filterKeyType: "boolean" as const,
            },
          ]
        : []),
    ],
    [showContainerTracking, showDiscrepancies, showStaffDiscrepancyControls]
  );

  const manifestColumns = useManifestColumns({
    showDiscrepancies,
    showSourceDocument,
    showContainerTracking,
    onDiscrepancyClick,
    isStaff,
  });

  // Type sits second, after the document number. The number is what a reader
  // scans for and what they arrived looking for; the kind is what disambiguates
  // it once found, so it reads better beside the number than in front of it.
  const columns = useMemo(() => {
    const [documentNumber, ...restOfManifestColumns] = blankOnBillOfLading(manifestColumns);
    return withPaperworkQuerySupport(
      [documentNumber, kindColumn, ...restOfManifestColumns, ...BILL_OF_LADING_COLUMNS].filter(
        (column) => column !== undefined
      )
    );
  }, [manifestColumns]);

  return { columns, filterKeys };
}

/**
 * V2's discrepancy modal, opened from the Discrepancies cell. Reads the
 * manifest's entity id off the row's populated side: the row's own `id` is the
 * grid's composite `<source>:<entity id>`, which the discrepancy authorizer
 * would reject.
 */
function DiscrepancyDialog({
  row,
  isStaff,
  onClose,
}: {
  row: Row | null;
  isStaff: boolean;
  onClose: () => void;
}) {
  return (
    <ManifestDetailDialog
      mode="customer"
      // Customers see only resolved discrepancies in the modal; a staff viewer
      // on this customer grid keeps the open (active) view.
      customerView={!isStaff}
      open={row !== null}
      onClose={onClose}
      processedManifestId={row ? paperworkManifestId(row) : null}
      manifestTrackingNumber={row?.manifestTrackingNumber ?? null}
      facilityName={row?.facility?.name ?? null}
    />
  );
}
